import React, { useEffect } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";

import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
// Import icons needed for the header
import { ReactComponent as MapPinIcon } from "feather-icons/dist/icons/map-pin.svg";
import { ReactComponent as MailIcon } from "feather-icons/dist/icons/mail.svg";
import { ReactComponent as TwitterIcon } from "feather-icons/dist/icons/twitter.svg";
import { ReactComponent as FacebookIcon } from "feather-icons/dist/icons/facebook.svg";
// import { ReactComponent as PinterestIcon } from "feather-icons/dist/icons/pinterest.svg";
import { ReactComponent as InstagramIcon } from "feather-icons/dist/icons/instagram.svg";
import { ReactComponent as SearchIcon } from "feather-icons/dist/icons/search.svg";
import { ReactComponent as PhoneCallIcon } from "feather-icons/dist/icons/phone-call.svg";
import { ReactComponent as MessageCircleIcon } from "feather-icons/dist/icons/message-circle.svg"; // Or use a combined icon

const Header = tw.header`
  w-full mx-auto bg-white
`;

export const NavLinks = tw.div`inline-block`;

/* hocus: stands for "on hover or focus"
 * hocus:bg-primary-700 will apply the bg-primary-700 class on hover or focus
 */
export const NavLink = styled.a(({ isActive }) => [
  tw`
    text-lg my-2 lg:text-sm lg:mx-4 lg:my-0
    font-semibold tracking-wide transition duration-300
    pb-1 border-b-2 border-transparent
    hover:border-red-500 hocus:text-red-500 text-gray-600 !text-[12px]
  `,
  isActive && tw`border-red-500 text-red-500`, // Active link style
]);

export const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-red-600 text-gray-100
  hocus:bg-red-700 focus:shadow-outline
  border-b-0
  flex-shrink-0 // Prevent shrinking
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 ml-0! flex-shrink-0 mr-4`}; /* Added mr-4 */
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 px-3 py-1 items-center justify-between`;
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;
export const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden z-10 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-lg text-gray-900 bg-white`}
  ${NavLinks} {
    ${tw`flex flex-col items-start`}
  }
`);

export const DesktopNavLinks = tw.nav`
  // This component seems unused in the current render structure
`;

// Component for social icons in top bar
const SocialLinks = tw.div`flex items-center gap-x-3`;
const SocialLink = tw.a`inline-block text-white text-sm hocus:text-red-500 transition duration-300`;

// Component for top bar login/news/faq links
const TopNavLinks = tw.div`flex items-center gap-x-4 text-xs`;
const TopNavLink = tw.a`hocus:text-red-500 transition duration-300`;

// Component for the Contact Info on the right
const ContactInfo = tw.div`flex items-center gap-x-4 flex-shrink-0 ml-6`;
const ContactIcon = tw.div`
  w-10 h-10 rounded-full bg-red-100 border border-red-500
  flex items-center justify-center flex-shrink-0
`;
const ContactText = tw.div`flex flex-col text-sm text-gray-700`;
const PhoneNumber = tw.span`text-lg font-bold text-gray-800`;
const Hours = tw.span`text-xs text-gray-600`;

export default ({
  roundedHeaderButton = false, // Not used in this specific design but kept from original code
  logoLink, // Not directly used in desktop, logo is hardcoded
  links, // Not directly used in desktop, links are hardcoded
  className,
  collapseBreakpointClass = "lg",
}) => {
  const collapseBreakpointCss =
    collapseBreakPointCssMap[collapseBreakpointClass];

  const [fixedHeader, setFixedHeader] = React.useState(false);

  useEffect(() => {
    window.addEventListener("scroll", () => {
      if (window.scrollY > 80) {
        setFixedHeader(true);
      } else {
        setFixedHeader(false);
      }
    });
  }, []);

  const { animation, toggleNavbar, showNavLinks } = useAnimatedNavToggler();
  return (
    <Header className={className || "header-light"}>
      {/* Top Bar */}
      <div tw="w-full bg-gray-800 text-white text-xs py-2 px-4 sm:px-12 flex items-start md:items-center justify-between">
        {/* Left - Location and Email */}
        <div tw="flex flex-col md:flex-row items-start md:items-center !text-[12px] gap-x-6 gap-y-1">
          <div tw="flex items-center gap-x-2">
            <MapPinIcon tw="w-4 h-4 text-red-500" />
            <span>Nansana Heights Room F-18, Hoima Road</span>
          </div>
          <a
            href="mailto:godwin@wealthwise.co.ug"
            rel="noopener noreferrer"
            target="_blank"
            tw="flex items-center gap-x-2"
          >
            <MailIcon tw="w-4 h-4 text-red-500" />
            <span>godwin@wealthwise.co.ug</span>
          </a>
        </div>

        <div tw="flex items-center gap-x-3 md:gap-x-6 !text-[10px]">
          <TopNavLinks>
            <TopNavLink href="#FAQs">FAQs</TopNavLink>
          </TopNavLinks>
          <SocialLinks>
            <SocialLink href="/#">
              <TwitterIcon tw="w-4 h-4" />
            </SocialLink>
            <SocialLink href="/#">
              <FacebookIcon tw="w-4 h-4" />
            </SocialLink>
            <SocialLink href="/#">
              <InstagramIcon tw="w-4 h-4" />
            </SocialLink>
          </SocialLinks>
        </div>
      </div>

      <nav
        tw="hidden lg:flex flex-1 items-center w-full justify-between py-4 px-6 sm:px-8"
        css={collapseBreakpointCss.desktopNavLinks}
        style={
          fixedHeader
            ? {
                position: "fixed",
                top: 0,
                width: "100vw",
                backgroundColor: "white",
                zIndex: 100,
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                padding: "15px 30px",
                color: "black !important",
              }
            : {}
        }
      >
        <div tw="flex items-center w-full justify-between">
          <LogoLink href="/">
            <div tw="flex items-center">
              <img
                src="/wealthwise.png"
                alt="Finlon Logo"
                tw="h-auto mr-2 w-[200px]"
              />
            </div>
          </LogoLink>
          <NavLinks>
            <NavLink href="/#" isActive={true}>
              Home
            </NavLink>
            <NavLink href="#about-us">About</NavLink>
            <NavLink href="#services">Services</NavLink>
            <NavLink href="#benefits">Benefits</NavLink>
            <NavLink href="#loan-calculator">Loan Calculator</NavLink>
            <NavLink href="#contact">Contact</NavLink>
          </NavLinks>
          <div tw="flex items-center flex-shrink-0">
            <PrimaryLink
              css={roundedHeaderButton && tw`rounded-full`} // Keep original prop support
              href="#apply-loan"
            >
              Request Loan
            </PrimaryLink>
            <ContactInfo>
              <ContactIcon>
                <PhoneCallIcon tw="w-5 h-5 text-red-500" />
              </ContactIcon>
              <a href="tel:0702003882">
                <ContactText>
                  <PhoneNumber>0702-003 882</PhoneNumber>
                  <Hours>Mon to Fri: 9 am to 6 pm</Hours>
                </ContactText>
              </a>
            </ContactInfo>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation (Ignored as per instructions, keeping original code structure) */}
      <MobileNavLinksContainer
        css={collapseBreakpointCss.mobileNavLinksContainer}
        style={
          fixedHeader
            ? {
                position: "fixed",
                top: 0,
                width: "100vw",
                backgroundColor: "white",
                zIndex: 100,
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                padding: "15px 30px",
                color: "black !important",
              }
            : {}
        }
      >
        <LogoLink href="/">
          <div tw="flex items-center">
            <img
              src="/wealthwise.png"
              alt="Finlon Logo"
              tw="h-auto mr-2 w-[150px]"
            />
          </div>
        </LogoLink>
        <NavToggle
          onClick={toggleNavbar}
          className={showNavLinks ? "open" : "closed"}
        >
          {showNavLinks ? (
            <CloseIcon tw="w-6 h-6" />
          ) : (
            <MenuIcon tw="w-6 h-6" />
          )}
        </NavToggle>
        <MobileNavLinks
          initial={{ x: "150%", display: "none" }}
          animate={animation}
          css={collapseBreakpointCss.mobileNavLinks}
        >
          {/* Default mobile links or links prop if provided */}
          {links || (
            <NavLinks>
              <NavLink href="/#" isActive={true}>
                Home
              </NavLink>
              <NavLink href="#about-us">About</NavLink>
              <NavLink href="#services">Services</NavLink>
              <NavLink href="#benefits">Benefits</NavLink>
              <NavLink href="#loan-calculator">Loan Calculator</NavLink>
              <NavLink href="#contact">Contact</NavLink>
            </NavLinks>
          )}
        </MobileNavLinks>
      </MobileNavLinksContainer>
    </Header>
  );
};

/* The below code is for generating dynamic break points for navbar.
 * Using this you can specify if you want to switch
 * to the toggleable mobile navbar at "sm", "md" or "lg" or "xl" above using the collapseBreakpointClass prop
 * Its written like this because we are using macros and we can not insert dynamic variables in macros
 */

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex flex-col`, // Added flex-col for top/main nav stacking
    mobileNavLinksContainer: tw`sm:hidden`,
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex flex-col`, // Added flex-col
    mobileNavLinksContainer: tw`md:hidden`,
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex flex-col`, // Added flex-col
    mobileNavLinksContainer: tw`lg:hidden`,
  },
  xl: {
    mobileNavLinks: tw`xl:hidden`, // Corrected from lg:hidden
    desktopNavLinks: tw`xl:flex flex-col`, // Added flex-col, Corrected from lg:flex
    mobileNavLinksContainer: tw`xl:hidden`, // Corrected from lg:hidden
  },
};
